import axios from 'axios';

export interface HealthResponse {
  status: string;
  service: string;
  version: string;
  timestamp: string;
}

// Health endpoint is at /health (not /api/health)
const HEALTH_BASE_URL = process.env.REACT_APP_ADMIN_API_BASE_URL || '';

class HealthService {
  private static instance: HealthService;
  private healthData: HealthResponse | null = null;
  private healthCheckPromise: Promise<HealthResponse> | null = null;

  private constructor() {}

  public static getInstance(): HealthService {
    if (!HealthService.instance) {
      HealthService.instance = new HealthService();
    }
    return HealthService.instance;
  }

  public async checkHealth(): Promise<HealthResponse> {
    // If we already have a health check in progress, return that promise
    if (this.healthCheckPromise) {
      return this.healthCheckPromise;
    }

    // If we already have cached health data, return it immediately
    if (this.healthData) {
      return Promise.resolve(this.healthData);
    }

    // Start a new health check
    this.healthCheckPromise = this.performHealthCheck();
    
    try {
      const result = await this.healthCheckPromise;
      this.healthData = result;
      return result;
    } finally {
      this.healthCheckPromise = null;
    }
  }

  private async performHealthCheck(): Promise<HealthResponse> {
    try {
      console.log('🏥 [HEALTH] Checking service health...');
      
      const response = await axios.get(`${HEALTH_BASE_URL}/health`, {
        headers: {
          'accept': 'application/json'
        },
        timeout: 10000 // 10 second timeout
      });

      const healthData: HealthResponse = response.data;
      
      console.log('✅ [HEALTH] Service is healthy:', {
        status: healthData.status,
        service: healthData.service,
        version: healthData.version,
        timestamp: healthData.timestamp
      });

      return healthData;
    } catch (error: any) {
      console.error('❌ [HEALTH] Service health check failed:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url
      });
      
      throw new Error(
        error.response?.status 
          ? `Service unavailable (${error.response.status}: ${error.response.statusText})`
          : 'Service health check failed. Please check if the server is running.'
      );
    }
  }

  public getCachedHealthData(): HealthResponse | null {
    return this.healthData;
  }

  public clearCache(): void {
    this.healthData = null;
    this.healthCheckPromise = null;
  }
}

export default HealthService.getInstance();
